/**
 * Username availability + set, for the onboarding "pick a username" step.
 * GET /users/username-available?username=X → { available, reason }.
 * Set is done via PUT /users/profile { username, firstName } (server validates + 409 on conflict).
 *
 * See specs/onboarding-questionnaire/spec.md (Story 2).
 */

import type { AxiosResponse } from 'axios';
import { API_ENDPOINTS } from '../../../constants';

export type ApiClientFn = (endpoint: string, config?: unknown) => Promise<AxiosResponse>;

export type AvailabilityReason = 'invalid' | 'profanity' | 'taken' | 'error';

export interface UsernameAvailability {
  available: boolean;
  reason?: AvailabilityReason;
}

/** Ask the server whether a username is free. Network/5xx → { available:false, reason:'error' }. */
export async function checkUsernameAvailable(
  username: string,
  apiClientFn: ApiClientFn,
): Promise<UsernameAvailability> {
  try {
    const endpoint = `${API_ENDPOINTS.users.usernameAvailable}?username=${encodeURIComponent(username)}`;
    const response = await apiClientFn(endpoint);
    if (response.status >= 400) return { available: false, reason: 'error' };
    const data = response.data as { available?: boolean; reason?: AvailabilityReason };
    return { available: data?.available === true, reason: data?.reason };
  } catch {
    return { available: false, reason: 'error' };
  }
}

export type SetUsernameResult =
  | { ok: true }
  | { ok: false; reason: 'taken' | 'invalid' | 'error' };

/**
 * Persist the chosen username (also mirrored to the display name). A 409 means it was taken
 * between the availability check and save (race) — the caller returns to the username step.
 */
export async function setUsername(
  username: string,
  apiClientFn: ApiClientFn,
): Promise<SetUsernameResult> {
  try {
    const response = await apiClientFn(API_ENDPOINTS.users.profile, {
      method: 'PUT',
      data: { username, firstName: username },
    });
    if (response.status === 409) return { ok: false, reason: 'taken' };
    if (response.status === 400) return { ok: false, reason: 'invalid' };
    if (response.status >= 400) return { ok: false, reason: 'error' };
    return { ok: true };
  } catch (err) {
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status === 409) return { ok: false, reason: 'taken' };
    if (status === 400) return { ok: false, reason: 'invalid' };
    return { ok: false, reason: 'error' };
  }
}
